/* eslint-disable react/no-children-prop */
/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import { Switch, Route, Redirect } from 'react-router-dom';
import {
  Button, Snackbar, Dialog, DialogActions, DialogContent, DialogContentText, Card, CardContent, Typography, Box,
} from '@material-ui/core';
import { useAuthenticator } from '@aws-amplify/ui-react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { Auth, API } from 'aws-amplify';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import Header from './Header';
import LeftBar from './LeftBar';
import routes from '../config/routes';
import SelectedObjectsToDeployContext from '../config/SelectedAssetsContext';
import AuthorizationProvider from '../authorization/AuthorizationProvider';
import Restricted from '../authorization/Restricted';
import LoadingCard from './utils/LoadingCard';
import Login from '../screens/Login';
import Footer from './Footer';
import { onUpdateConfig } from '../config/graphql';
import markdownContent from '../CHANGELOG';
import { useSnackbar } from '../providers/SnackbarContext';
import GraphqlService from '../service/graphqlService';
import useConfig from '../hooks/useGetConfig';
import useRole from '../hooks/useGetRole';
import Maintenance from '../screens/Maintenance';
import { hooks } from '../config/hooksLoader'; // Add this import
import useMaintenanceSubscription from '../hooks/useMaintenanceSubscription';
import getCookieValue from '../utils/getCookieValue';
import NoProjectsPage from './NoProjectsPage';
import { target } from '../config/awsExports'; // Added import
import '../styles/changelog-markdown.css'; // Import the improved changelog-markdown.css
import useAnalytics from '../hooks/useAnalytics'; // Import useAnalytics
import RubiqAgentChat from './chat/RubiqAgentChat'; // Import RubiqAgentChat
import { useProject } from '../config/ProjectContext'; // Import useProject hook
import useRegionFeatureGate from '../hooks/useRegionFeatureGate';
import useNetworkRestriction from '../hooks/useNetworkRestriction';
import NetworkRestriction from '../screens/NetworkRestriction/NetworkRestriction';

// Utility function to convert the changelog array to markdown format
const convertChangelogToMarkdown = (changelogData) => {
  if (!Array.isArray(changelogData)) return '';

  let markdown = '# 📜 **Changelog**\n\n';
  markdown += 'All notable changes to this project will be documented here.';
  markdown += 'The format follows [Keep a Changelog](https://keepachangelog.com/en/1.0.0/) and adheres to [Semantic Versioning](https://semver.org/).\n\n';

  changelogData.forEach((entry) => {
    // add here an extra space between versions
    markdown += '<br />\n\n';
    markdown += `## 🚀 **Version ${entry.version}** - *${entry.releaseDate}*\n\n`;

    if (entry.new && entry.new.length > 0) {
      markdown += '### ✨ **New Features**\n\n';
      entry.new.forEach((item) => {
        markdown += `- **${item.title}**: ${item.description}\n`;
      });
      markdown += '\n';
    }

    if (entry.updated && entry.updated.length > 0) {
      markdown += '### 🔄 **Updates**\n\n';
      entry.updated.forEach((item) => {
        markdown += `- **${item.title}**: ${item.description}\n`;
      });
      markdown += '\n';
    }

    if (entry.fix && entry.fix.length > 0) {
      markdown += '### 🐛 **Bug Fixes**\n\n';
      entry.fix.forEach((item) => {
        markdown += `- **${item.title}**: ${item.description}\n`;
      });
      markdown += '\n';
    }

    markdown += '\n\n'; // Add extra space between versions for better readability
  });

  return markdown;
};

const drawerWidth = 280;
const drawerCollapsedWidth = 64;

const useStyles = makeStyles((theme) => ({
  root: {
    marginLeft: 'auto',
    marginRight: 'auto',
    '& .MuiFilledInput-root': {
      backgroundColor: theme.palette.common.white,
    },
    '& .MuiSelect-select:focus': {
      backgroundColor: theme.palette.common.white,
    },
  },
  content: {
    backgroundColor: theme.palette.common.white,
    paddingBottom: 56,
  },
  mainContainer: {
    display: 'flex',
    flexDirection: 'column',
    zIndex: theme.zIndex.appBar - 1,
    padding: theme.spacing(0),
    [theme.breakpoints.up('md')]: {
      marginLeft: (props) => (props.sidebarCollapsed ? drawerCollapsedWidth : drawerWidth),
      transition: 'margin-left 200ms ease-in-out',
    },
  },
  newHome: {
    backgroundColor: theme.palette.common.white,
    color: '#fff',
    padding: '10px',
    display: 'flex',
    justifyContent: 'center',
    gap: '6px',
  },
  linkToNewPortal: {
    color: '#fff',
    textDecoration: 'none',
    background: 'blue',
    paddingLeft: '4px',
    paddingRight: '4px',
  },
}));

function RoutesComponent({ openSnackBar }) {
  return (
    <Switch>
      {routes.map(({ path: routespath, component: Component, permission }) => (
        <Route
          exact
          path={routespath}
          key={routespath}
          render={(props) => (
            <Restricted
              to={Array.isArray(permission) ? permission : [permission]}
              loadingComponent={<LoadingCard />}
            >
              <Component {...props} openSnackBar={openSnackBar} />
            </Restricted>
          )}
        />
      ))}
      <Redirect to="/" />
    </Switch>
  );
}

function MainComponent({
  open, setOpen, openChangeLogModal, snackBarOpen, setSnackBarOpen, snackBarText,
  selectedAssetsProviderValue, handleDrawer, handleOpenChangeLogModal, handleCloseChangeLogModal,
  openSnackBar, permissionsFetched, classes, fetchPermissions, route,
  sidebarCollapsed, setSidebarCollapsed,
}) {
  const { setProject, restoreLastUsedProject } = useProject();
  const isFeatureAvailable = useRegionFeatureGate();
  const fetchProjects = useCallback(async () => GraphqlService.getProjects(), []);
  const dialogRef = useRef(null);
  const [isCheckingPermissions, setIsCheckingPermissions] = useState(false);
  const maxLoginAttempts = 2; // Maximum number of retry attempts
  const LOGIN_ATTEMPTS_KEY = 'loginAttemptsCount';
  const MAX_ATTEMPTS_REACHED_KEY = 'maxLoginAttemptsReached';
  
  const {
    data: projectsResponse,
  } = useQuery(['projectsLayout'], fetchProjects, {
    refetchOnWindowFocus: false, // Disable refetching on window focus
    refetchOnReconnect: false, // Disable refetching on reconnect
    refetchInterval: false, // Disable polling
    enabled: route === 'authenticated'
  });

  // Define placeholder constants for undefined variables.
  // Replace these with actual values from your configuration or environment.
  const SSO_ENABLED_COOKIE = 'ssoEnabled'; // Updated cookie name

  const clearCognitoKeys = () => {
    // Iterate through all keys in localStorage
    Object.keys(localStorage).forEach((key) => {
      // Check if the key includes 'CognitoIdentityServiceProvider'
      if (key.includes('CognitoIdentityServiceProvider')) {
        localStorage.removeItem(key); // Remove the matching key
      }
    });
  };

  // Get login attempts from localStorage
  const getLoginAttempts = () => {
    const attempts = localStorage.getItem(LOGIN_ATTEMPTS_KEY);
    return attempts ? parseInt(attempts, 10) : 0;
  };

  // Set login attempts in localStorage
  const setLoginAttempts = (count) => {
    localStorage.setItem(LOGIN_ATTEMPTS_KEY, count.toString());
  };

  // Check if max attempts reached
  const hasReachedMaxAttempts = () => {
    return localStorage.getItem(MAX_ATTEMPTS_REACHED_KEY) === 'true';
  };

  // Set max attempts reached flag
  const setMaxAttemptsReached = (reached) => {
    if (reached) {
      localStorage.setItem(MAX_ATTEMPTS_REACHED_KEY, 'true');
    } else {
      localStorage.removeItem(MAX_ATTEMPTS_REACHED_KEY);
    }
  };

  // Reset login attempts
  const resetLoginAttempts = () => {
    localStorage.removeItem(LOGIN_ATTEMPTS_KEY);
    localStorage.removeItem(MAX_ATTEMPTS_REACHED_KEY);
  };

  // Restore last used project when projects are fetched
  useEffect(() => {
    if (projectsResponse && projectsResponse.length > 0 && route === 'authenticated') {
      // Set setDefaultIfNotFound to true to use first project if stored project is invalid or doesn't exist
      const restoredProject = restoreLastUsedProject(projectsResponse, true);
      
      if (restoredProject) {
        console.log('Restoring project:', restoredProject.name);
        setProject(restoredProject);
      }
    }
  }, [projectsResponse, route, setProject, restoreLastUsedProject]);

  useEffect(() => {
    async function handleAuthAndProjectRedirect() {
      // Don't run if we've already reached max attempts
      if (hasReachedMaxAttempts()) {
        console.log('Max attempts already reached (from localStorage), skipping redirect logic');
        setIsCheckingPermissions(false);
        return;
      }

      // Don't run if route is not authenticated
      if (route !== 'authenticated') {
        return;
      }

      // Wait for projectsResponse to be defined
      if (projectsResponse === undefined) {
        return;
      }

      try {
        if (projectsResponse && projectsResponse.length > 0) {
          console.log('Projects found, setting cookie and resetting attempts.');
          document.cookie = `${SSO_ENABLED_COOKIE}=true; path=/; max-age=${60 * 60 * 24 * 7}`; // Expires in 7 days
          resetLoginAttempts(); // Reset counter on success
          setIsCheckingPermissions(false);
          return;
        }
        
        // No projects found - check if we should retry login
        const currentAttempts = getLoginAttempts();
        console.log(`No projects found. Login attempts: ${currentAttempts}/${maxLoginAttempts}`);
        
        if (currentAttempts >= maxLoginAttempts) {
          console.log('Max login attempts reached. Showing no permissions page permanently.');
          setMaxAttemptsReached(true);
          setIsCheckingPermissions(false);
          return;
        }
        
        // Only proceed if we haven't tried yet or still have attempts left
        // DON'T call federatedSignIn with undefined provider - it causes reload loop
        console.log('Max attempts not yet reached, but no valid SSO provider. Showing no permissions page.');
        setLoginAttempts(currentAttempts + 1);
        setIsCheckingPermissions(false);
        
        // Don't call Auth.federatedSignIn if provider is undefined
        // This was causing the infinite loop
        return;

      } catch (error) {
        console.error('Error during project fetching or federated sign-in:', error);
        setIsCheckingPermissions(false);
      }
    }

    handleAuthAndProjectRedirect();
  }, [route, projectsResponse]); // Use projectsResponse instead of fetchProjects

  // Stop loading when projects are fetched
  useEffect(() => {
    if (projectsResponse !== undefined) {
      setIsCheckingPermissions(false);
    }
  }, [projectsResponse]);

  // Safety timeout to prevent infinite loading
  useEffect(() => {
    if (isCheckingPermissions) {
      const timeout = setTimeout(() => {
        setIsCheckingPermissions(false);
      }, 30000); // 30 seconds timeout
      
      return () => clearTimeout(timeout);
    }
  }, [isCheckingPermissions]);

  // Convert the changelog array to markdown format
  const changelogMarkdown = convertChangelogToMarkdown(markdownContent);

  return (
    <>
      {route === 'authenticated'
        ? (
          <SelectedObjectsToDeployContext.Provider value={selectedAssetsProviderValue}>
            <AuthorizationProvider fetchPermissions={fetchPermissions}>
              <div className={classes.root}>
                <CssBaseline />
                <main className={classes.content}>
                  <LeftBar open={open} setOpen={setOpen} collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed} />
                  <div className={classes.mainContainer}>
                    <Header handleDrawer={handleDrawer} handleOpenChangeLogModal={handleOpenChangeLogModal} projects={projectsResponse} />
                    {permissionsFetched && <RoutesComponent openSnackBar={openSnackBar} />}
                  </div>
                  {isCheckingPermissions && (
                    <Box className="flex items-center justify-center min-h-screen bg-gray-50">
                      <div className="text-center bg-white p-8 rounded-lg shadow-lg max-w-md">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                        <h2 className="text-xl font-semibold text-gray-800 mb-2">Setting up your account</h2>
                        <p className="text-lg text-gray-600 mb-2">Please wait... we are checking your permissions</p>
                        <p className="text-sm text-gray-500">This may take a few moments as we verify your access rights</p>
                      </div>
                    </Box>
                  )}
                  {!isCheckingPermissions && projectsResponse && projectsResponse.length === 0 && (
                    <Box>
                      <NoProjectsPage />
                    </Box>

                  )}
                  {/* <CookieConsent /> */}
                  {/* Rubiq Agent chat is not available in the China data region. */}
                  {isFeatureAvailable('rubiqAgent') && <RubiqAgentChat />}
                  <Dialog ref={dialogRef} open={openChangeLogModal} onClose={handleCloseChangeLogModal} aria-labelledby="changelog-title" fullWidth maxWidth="sm">
                    <DialogContent ref={dialogRef} style={{ position: 'relative', paddingTop: '56px' }}>
                      <button
                        onClick={handleCloseChangeLogModal}
                        style={{
                          position: 'absolute',
                          top: '16px',
                          right: '16px',
                          background: 'linear-gradient(135deg, #f5f5f5 0%, #efefef 100%)',
                          border: '1px solid #e0e0e0',
                          cursor: 'pointer',
                          padding: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '8px',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          color: '#424242',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'linear-gradient(135deg, #e8e8e8 0%, #dcdcdc 100%)';
                          e.target.style.color = '#1a1a1a';
                          e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.12)';
                          e.target.style.transform = 'scale(1.08)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'linear-gradient(135deg, #f5f5f5 0%, #efefef 100%)';
                          e.target.style.color = '#424242';
                          e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.08)';
                          e.target.style.transform = 'scale(1)';
                        }}
                        aria-label="Close changelog"
                      >
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                      <DialogContentText id="changelog-text">
                        <Card className={classes.card}>
                          <CardContent>
                            <Typography component="div" variant="body1" className={classes.markdown}>
                              <ReactMarkdown
                                children={changelogMarkdown}
                                rehypePlugins={[rehypeRaw]}
                                className="changelog-markdown"
                              />
                            </Typography>
                          </CardContent>
                        </Card>
                      </DialogContentText>
                    </DialogContent>
                  </Dialog>
                  <Snackbar
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    open={snackBarOpen}
                    autoHideDuration={2000}
                    message={snackBarText}
                    action={(
                      <Button color="inherit" size="small" onClick={() => setSnackBarOpen(false)}>
                        Close
                      </Button>
                    )}
                    onClose={() => setSnackBarOpen(false)}
                    key="snackbar"
                  />
                </main>
                <Footer />
              </div>
            </AuthorizationProvider>
          </SelectedObjectsToDeployContext.Provider>
        )
        : <Login />}
    </>
  );
}

function PortalInMaintenanceBannerForRoot({ classes, isInMaintenance, role }) {
  return (
    <>
      {isInMaintenance && role.includes('rootAccess') && (
        <div className={classes.newHome}>
          <div>Portal is being updated</div>
        </div>
      )}
    </>
  );
}

export default function Layout() {
  const [sidebarCollapsed, setSidebarCollapsedState] = useState(() => {
    try {
      const stored = localStorage.getItem('iotPortal.sidebarCollapsed');
      if (stored === null) return true;
      return stored === 'true';
    } catch {
      return true;
    }
  });
  const setSidebarCollapsed = (value) => {
    setSidebarCollapsedState(value);
    try { localStorage.setItem('iotPortal.sidebarCollapsed', String(value)); } catch { /* ignore */ }
  };
  const classes = useStyles({ sidebarCollapsed });

  // Default value for Models combo
  const [permissionsFetched, setPermissionsFetched] = useState(false);
  const [selectedObjectsToDeploy, setSelectedObjectsToDeploy] = useState([]);
  const [open, setOpen] = useState(false);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackBarText, setSnackBarText] = useState('');
  const [isInMaintenance, setIsInMaintenance] = useState(false);
  const selectedAssetsProviderValue = { selectedObjectsToDeploy, setSelectedObjectsToDeploy };
  const [openChangeLogModal, setOpenChangeLogModal] = useState(false);
  const openSnackBar = useSnackbar();
  const queryClient = useQueryClient();
  const { configData } = useConfig();
  useMaintenanceSubscription();
  const [lastLoginDateUpdated, setLastLoginDateUpdated] = useState(false); // To ensure it runs once per login
  const subscribeToNotifications = async () => {
    try {
      const subscription = API.graphql({
        query: onUpdateConfig,
        authMode: 'AMAZON_COGNITO_USER_POOLS',
        variables: {
          authToken: Auth.user?.attributes?.sub || Auth.user?.signInUserSession?.accessToken?.payload?.sub,
        },
      }).subscribe({
        next: () => {
          queryClient.invalidateQueries(['config']);
        },
        error: (error) => {
          console.log(`Error subscribing to config update: ${error}`, 'error');
        },
      });
      // Cleanup function to unsubscribe
      return () => subscription.unsubscribe();
    } catch (error) {
      console.log(`Error setting up subscription to config update: ${error}`, 'error');
      return null;
    }
  };

  useEffect(() => {
    subscribeToNotifications();
  }, []);

  const handleDrawer = () => {
    setOpen(!open);
  };

  const handleOpenChangeLogModal = () => {
    setOpenChangeLogModal(true);
  };

  const handleCloseChangeLogModal = () => {
    setOpenChangeLogModal(false);
  };

  const fetchPermissions = async (projectId) => {
    try {
      // Assuming hooks.permissionsEndpoint.getPermissions is the correct function
      const result = await hooks.permissionsEndpoint.getPermissions(projectId);
      setPermissionsFetched(true);
      return result;
    } catch (error) {
      openSnackBar('Error fetching permissions');
      return [];
    }
  };

  const { route } = useAuthenticator((context) => [context.route]);
  const { isRestricted } = useNetworkRestriction({ enabled: route === 'authenticated' });

  const { role } = useRole();

  const cookieRootAccess = getCookieValue('rootAccess') === 'true';

  useEffect(() => {
    setIsInMaintenance(configData?.maintenance === 'true' && !cookieRootAccess);
  }, [configData, cookieRootAccess]);

  useEffect(() => {
    if (route === 'authenticated' && !lastLoginDateUpdated && !isRestricted) {
      const updateUserLastLogin = async () => {
        try {
          const currentUser = await Auth.currentAuthenticatedUser();
          // Assuming the username is in attributes.email or username directly
          const username = currentUser.username || currentUser.attributes?.email;
          if (username) {
            await GraphqlService.updateUserLastLoginDate(username);
            setLastLoginDateUpdated(true); // Mark as updated for this session
          } else {
            console.warn('Could not determine username after authentication to update last login date.');
          }
        } catch (error) {
          console.error('Error fetching current user or updating last login date:', error);
        }
      };

      updateUserLastLogin();
    } else if (route !== 'authenticated') {
      // Reset if the user is no longer authenticated (e.g., signed out)
      setLastLoginDateUpdated(false);
    }
  }, [route, lastLoginDateUpdated, isRestricted]);

  const getProjects = async () => GraphqlService.getProjects();
  const { data: projectsData } = useQuery(['getProjects'], getProjects, {
    cacheTime: 1000,
  });
  const { setAnalytics } = useAnalytics();

  useEffect(() => {
    if (!setAnalytics || !projectsData) return;
    if (route === 'authenticated') {
      // set projects code as a string split by commas
      const projects = projectsData?.map((project) => ({
        projectCode: project?.code,
        roles: project?.role || [],
      }));
      setAnalytics({
        event: 'user_identified',
        extraParams: { projects },
      });
    }
  }, [route, projectsData, setAnalytics]);

  // Must stay after the last hook of Layout (Rules of Hooks) and win over the maintenance and main branches.
  if (route === 'authenticated' && isRestricted) return <NetworkRestriction />;

  // Use the value of route to decide which page to render
  return (
    <>
      <PortalInMaintenanceBannerForRoot classes={classes} isInMaintenance={isInMaintenance} role={role} />
      {(isInMaintenance && role && role !== 'rootAccess' && !cookieRootAccess) ? <Maintenance className={classes.root} /> : (
        <MainComponent
          open={open}
          setOpen={setOpen}
          sidebarCollapsed={sidebarCollapsed}
          setSidebarCollapsed={setSidebarCollapsed}
          openChangeLogModal={openChangeLogModal}
          setOpenChangeLogModal={setOpenChangeLogModal}
          snackBarOpen={snackBarOpen}
          setSnackBarOpen={setSnackBarOpen}
          snackBarText={snackBarText}
          setSnackBarText={setSnackBarText}
          selectedAssetsProviderValue={selectedAssetsProviderValue}
          handleDrawer={handleDrawer}
          handleOpenChangeLogModal={handleOpenChangeLogModal}
          handleCloseChangeLogModal={handleCloseChangeLogModal}
          openSnackBar={openSnackBar}
          permissionsFetched={permissionsFetched}
          classes={classes}
          fetchPermissions={fetchPermissions}
          route={route}
        />
      )}
    </>
  );
}

// Add this style block at the end of the file (or in your CSS):
// .changelog-markdown h1, .changelog-markdown h2, .changelog-markdown h3 {
//   margin-top: 1.5em;
//   margin-bottom: 0.5em;
//   font-weight: bold;
// }
// .changelog-markdown ul {
//   margin-left: 1.5em;
//   margin-bottom: 1em;
// }
// .changelog-markdown li {
//   margin-bottom: 0.5em;
// }
// .changelog-markdown {
//   line-height: 1.7;
//   font-size: 1.05rem;
// }
